'use client';

import React from 'react';

import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import CardWrapper from '@/components/layout/CardWrapper/CardWrapper';

interface ContentCardProps {
  image?: string;
  iconText?: string; // For the "Our Values" circle icon
  heading?: any;
  subheading?: string;
  description?: any;
  button?: React.ReactNode;
  variant?: 'image' | 'icon';
  imageMode?: 'cover' | 'natural';
  imageClassName?: string;
  className?: string;
  /** Optional classes for the numbered/letter circle when variant is "icon". */
  iconClassName?: string;
  text?: string;
  fontStyle?: string;
  textSize?: string;
  textColor?: string;
}

const ContentCard: React.FC<ContentCardProps> = ({
  image,
  iconText,
  heading,
  subheading,
  description,
  text,
  button,
  variant = 'image',
  imageMode = 'cover',
  imageClassName = '',
  className = '',
  iconClassName = '',
  fontStyle = 'font-bold',
  textSize = 'text-xl md:text-2xl',
  textColor = '',
}) => {
  return (
    <CardWrapper
      width="full"
      columns="one-column"
      paddingClassName={`p-6 ${className}`.trim()}
    >
      <div className="flex h-full flex-col space-y-4">
        {/* Render Circle Icon if variant is icon */}
        {variant === 'icon' && iconText && (
          <div
            className={`bg-info-450 flex h-10 w-10 items-center justify-center rounded-full font-bold text-white ${iconClassName}`}
          >
            {iconText}
          </div>
        )}

        {/* Render Image if variant is image */}
        {variant === 'image' && image && (
          <div
            className={`relative w-full overflow-hidden rounded-lg ${
              imageClassName || (imageMode === 'cover' ? 'h-75' : 'h-auto')
            }`}
          >
            <R2Image
              path={image}
              alt={image || ''}
              // If natural, we don't use 'fill' so it keeps its aspect ratio
              {...(imageMode === 'cover'
                ? { fill: true, className: 'object-cover' }
                : {
                    width: 800,
                    height: 1000,
                    className: 'w-full h-auto object-contain',
                  })}
            />
          </div>
        )}

        {/* Heading and Description */}
        <div>
          {heading &&
            (typeof heading === 'string' ? (
              <h3
                className={`${textColor} ${textSize} white leading-tight font-bold *:m-0`}
              >
                {heading}
              </h3>
            ) : (
              <RichText
                styles={{
                  p: `text-xl md:text-2xl font-bold *:m-0 leading-tight`,
                }}
              >
                {heading}
              </RichText>
            ))}
          {subheading && (
            <p className="text-royal-500 text-xl font-bold whitespace-pre-line">
              {subheading}
            </p>
          )}
          {text && <p className="text-royal-500 text-xl font-bold">{text}</p>}
        </div>

        {description &&
          (typeof description === 'string' ? (
            <p className={`leading-snug text-gray-700 ${fontStyle}`}>
              {description}
            </p>
          ) : (
            <RichText
              styles={{
                p: `text-gray-700 leading-relaxed [&_p]:leading-5 ${fontStyle}`,
              }}
            >
              {description}
            </RichText>
          ))}

        {button && <div className="mt-auto pt-4">{button}</div>}
      </div>
    </CardWrapper>
  );
};

export default ContentCard;
